import { Modal, notification } from "antd";
import cuid from "cuid";
import GeneralCosts from "entities/GeneralCosts";
import { find, get } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import edit from "shared/assets/icons/generate.svg";
import { useLazyQuery, useMutation, useQuery } from "shared/hooks/useApi";
import useStaticAttachments from "shared/hooks/useStaticAttachments";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import { GENERATE_QUOTE_DOCUMENTS } from "utils/api/graphql/mutations/attachment";
import { CREATE_SUBSCRIPTION } from "utils/api/graphql/mutations/subscription";
import { TRACER } from "utils/api/graphql/queries/tracers";
import {
  CONTACT_POINT_SYSTEM,
  DOCUMENT_TEMPLATE_AREAS,
  INSURANCE_PLAN_ATTACHMENTS_TYPES,
  PROCESS_TYPES,
  SUBSCRIPTION_STATUSES,
  TEMPLATES,
  THEMES_PALETTE,
} from "utils/constants";
import { removeDuplicates } from "utils/helpers/array";
import { areasOverlap } from "utils/helpers/documentTemplates";
import ExpirationModal from "../Widgets/ExpirationModal";
import View from "./View";

const documentFileName = ({ outputName, type }, t) =>
  outputName ||
  t(`DYNAMIC_DOCUMENT_FILE_NAMES.${type}`, {
    defaultValue: type || t("DYNAMIC_DOCUMENT_FILE_NAMES.DEFAULT"),
  });

const Quote = ({
  project,
  onNext,
  onBack,
  tracer,
  author,
  setDisplayProject,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.PROJECT);
  const DETAIL = t("DETAIL", { returnObjects: true });
  const [addSubscription] = useMutation(CREATE_SUBSCRIPTION);
  const [nextLoading, setNextLoading] = useState(false);

  const [generateQuoteDocuments] = useMutation(GENERATE_QUOTE_DOCUMENTS);
  const [attachments, setAttachments] = useState([]);

  const selectedPlans =
    get(project, "fields.lastSelected", [])?.length > 0
      ? get(project, "fields.lastSelected", [])
      : get(project, "fields.insurancePlans", []);

  useQuery(graphql.queries.INSURANCE_PLANS, {
    variables: {
      isIn: { id: selectedPlans },
    },
    onCompleted: (data) => {
      const dynamicDocumentConfiguration = removeDuplicates(
        get(data, "insurancePlans.data", [])
          .map((plan) => get(plan, "documentTemplates", []))
          .flat()
          .filter(({ area }) =>
            areasOverlap(area, DOCUMENT_TEMPLATE_AREAS.QUOTE),
          )
          .map((config) => ({
            contentType: "application/pdf",
            type: get(config, "type"),
            name: `${documentFileName(config, t)}.pdf`,
          })),
      );
      setAttachments((prev) => [...prev, ...dynamicDocumentConfiguration]);
    },
  });

  const { loading } = useStaticAttachments({
    projectId: get(project, "id"),
    insurancePlanIds: selectedPlans,
    types: Object.values(INSURANCE_PLAN_ATTACHMENTS_TYPES),
    onCompleted: (resolved) => setAttachments((prev) => [...prev, ...resolved]),
  });

  const [addLink] = useMutation(graphql.mutations.CREATE_LINK);
  const [getTracer] = useLazyQuery(TRACER);

  const [sendQuoteMutation] = useMutation(graphql.mutations.SEND_QUOTE);

  const [updateContact] = useMutation(graphql.mutations.UPDATE_CONTACT, {
    onCompleted: async () => {
      const prospectTheme =
        THEMES_PALETTE[get(tracer, "flow.process.theme")].PROSPECT_PROCESS;
      try {
        const {
          data: { tracer: tracerData },
        } = await getTracer({
          variables: {
            where: {
              flow: {
                process: {
                  theme: prospectTheme,
                },
              },
            },
          },
        });

        const selectedData = get(project, "fields.lastSelected", []);
        const insurancePlans = selectedData.map((id) => ({ id }));
        const { errors } = await generateQuoteDocuments({
          variables: {
            data: {
              project: { id: project.id },
              insurancePlans,
            },
          },
        });

        if (errors) {
          setNextLoading(false);
          throw new Error(`GraphQL Error: ${JSON.stringify(errors)}`);
        }

        let links = {};
        const existingSubscriptions = get(project, "subscriptions", []);
        const createdSubscriptions = [];
        const createSubscription = async (insurancePlanId) => {
          const existingSubscription = existingSubscriptions.find(
            (subscription) =>
              get(subscription, "insurancePlan.id") === insurancePlanId,
          );

          let subscriptionId;
          let generatedUrl;

          if (existingSubscription) {
            subscriptionId = existingSubscription.id;
            generatedUrl = get(
              existingSubscription,
              "insurancePlan.config.generatedUrl",
            );
          } else {
            const { data: subscriptionData } = await addSubscription({
              variables: {
                data: {
                  id: cuid(),
                  project: { id: project.id },
                  insurancePlan: { id: insurancePlanId },
                },
              },
            });
            subscriptionId = get(subscriptionData, "addSubscription.id");
            generatedUrl = get(
              subscriptionData,
              "addSubscription.insurancePlan.config.generatedUrl",
            );
            createdSubscriptions.push({
              id: subscriptionId,
              status: SUBSCRIPTION_STATUSES.DRAFT,
              insurancePlan: { id: insurancePlanId },
            });
          }

          if (get(project, "fields.addLinks")) {
            const statuses = get(
              tracerData,
              "flow.process.processStatuses",
              [],
            );
            const selectedStatus = find(statuses, {
              template:
                TEMPLATES[PROCESS_TYPES.B2C].THEMES[prospectTheme]
                  .EFFECTIVE_DATE,
            })?.id;

            const url =
              generatedUrl ||
              `${process.env.REACT_APP_BASE_URL}/#/public/process/init/${get(
                tracerData,
                "source.id",
              )}`;

            const { data: linkData } = await addLink({
              variables: {
                data: {
                  url,
                  subscription: {
                    id: subscriptionId,
                  },
                  project: { id: project.id },
                  fields: {
                    process: { id: get(tracerData, "flow.process.id") },
                    status: { id: selectedStatus },
                  },
                },
              },
            });

            links[insurancePlanId] = `${url}?l=${linkData.addLink.id}`;
          }
        };

        await Promise.all(selectedData.map(createSubscription));

        await sendQuoteMutation({
          variables: {
            data: {
              links,
              project: { id: project.id },
              insurancePlans,
            },
          },
          onCompleted: () => {
            notification.open({
              message: DETAIL.TEMPLATES.QUOTE.NOTIFICATION.SUCCESS,
              duration: 5,
              type: "success",
            });
            onNext({
              payload: {
                locked: true,
                fields: {
                  ...project.fields,
                  insurancePlans: [
                    ...get(project, "fields.insurancePlans", []),
                    ...get(project, "fields.lastSelected", []),
                  ],
                },
              },
              onCompleted: () => {
                if (createdSubscriptions.length && setDisplayProject) {
                  setDisplayProject((prev) => {
                    const existingIds = get(prev, "subscriptions", []).map(
                      ({ id: subscriptionId }) => subscriptionId,
                    );
                    return {
                      ...prev,
                      subscriptions: [
                        ...get(prev, "subscriptions", []),
                        ...createdSubscriptions.filter(
                          ({ id: subscriptionId }) =>
                            !existingIds.includes(subscriptionId),
                        ),
                      ],
                    };
                  });
                }
                setNextLoading(false);
              },
              onError: () => setNextLoading(false),
            });
          },
        });
      } catch (_error) {
        setNextLoading(false);
      }
    },
  });

  const sendQuote = (telecoms) => {
    setNextLoading(true);
    updateContact({
      variables: {
        where: {
          id: project.contact.id,
        },
        data: {
          telecoms: [
            ...project.contact.telecoms.filter(
              (telecom) => telecom.system !== CONTACT_POINT_SYSTEM.EMAIL,
            ),
            ...telecoms.map(({ label, value }) => ({
              id: value,
              system: CONTACT_POINT_SYSTEM.EMAIL,
              value: label,
            })),
          ],
        },
      },
    });
  };

  const generalCostsFilter = {
    where: {
      project: {
        id: project.id,
      },
    },
    isIn: {
      insurancePlan: {
        id: get(project, "fields.lastSelected", []),
      },
    },
  };

  return (
    <>
      <ExpirationModal project={project} />
      {nextLoading && (
        <Modal open width={"60%"} footer={null}>
          <div className="modal-info">
            <img src={edit} alt={"en cours"} />
            <div className="modal-info__text">
              <h4>{DETAIL.TEMPLATES.QUOTE.IN_PROGRESS}</h4>
              <p>{DETAIL.TEMPLATES.QUOTE.GENERATING_DOCUMENTS}</p>
              <p>{DETAIL.TEMPLATES.QUOTE.FEW_SECONDS}</p>
            </div>
          </div>
        </Modal>
      )}
      <GeneralCosts
        filter={generalCostsFilter}
        loading={loading}
        updateContact={updateContact}
        attachments={attachments}
        setAttachments={setAttachments}
        project={project}
        onNext={sendQuote}
        nextLoading={nextLoading}
        onBack={onBack}
        View={View}
        NoData={View}
        author={author}
        tracer={tracer}
      />
    </>
  );
};

export default Quote;
